// ForecastingChart.jsx
import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Scatter,
  Legend,
} from "recharts";

/** 5-point star for CI markers */
const StarDot = ({ cx, cy, fill = "#1E57BE", size = 8 }) => {
  if (cx == null || cy == null) return null;
  const r = size / 2,
    spikes = 5,
    outer = r,
    inner = r * 0.5,
    step = Math.PI / spikes;
  const pts = [];
  let a = -Math.PI / 2; // up
  for (let i = 0; i < spikes; i++) {
    pts.push(`${cx + Math.cos(a) * outer},${cy + Math.sin(a) * outer}`);
    a += step;
    pts.push(`${cx + Math.cos(a) * inner},${cy + Math.sin(a) * inner}`);
    a += step;
  }
  return <polygon points={pts.join(" ")} fill={fill} />;
};

/** Merge history + forecast into one dataset */
function toChartData(result) {
  const iYears = Array.isArray(result?.input_years) ? result.input_years : [];
  const input = Array.isArray(result?.input) ? result.input : [];
  const fYears = Array.isArray(result?.years) ? result.years : [];
  const fcast = Array.isArray(result?.forecast) ? result.forecast : [];
  const u80 = Array.isArray(result?.upper_80) ? result.upper_80 : [];
  const l80 = Array.isArray(result?.lower_80) ? result.lower_80 : [];
  const u95 = Array.isArray(result?.upper_95) ? result.upper_95 : [];
  const l95 = Array.isArray(result?.lower_95) ? result.lower_95 : [];

  const historyRows = iYears.map((yr, i) => ({
    year: Number(yr),
    history: typeof input[i] === "number" ? input[i] : null,
    forecast: null,
    upper_80: null,
    lower_80: null,
    upper_95: null,
    lower_95: null,
  }));

  const forecastRows = fYears.map((yr, i) => ({
    year: Number(yr),
    history: null,
    forecast: typeof fcast[i] === "number" ? fcast[i] : null,
    upper_80: u80[i],
    lower_80: l80[i],
    upper_95: u95[i],
    lower_95: l95[i],
  }));

  // merge & sort by year (just in case)
  const rows = [...historyRows, ...forecastRows].sort(
    (a, b) => a.year - b.year
  );
  return rows;
}

export default function ForecastChartRecharts({
  result,
  title,
  yLabel,
  width = 400,
  height = 320,
}) {
  const data = toChartData(result);
  const years = data.map((d) => d.year);
  const hasData = data.length && years.every((y) => Number.isFinite(y));

  if (!hasData)
    return <div style={{ padding: 12 }}>No forecast data to display.</div>;

  const minX = Math.min(...years);
  const maxX = Math.max(...years);

  const COLORS = {
    history: "#000000",
    forecast: "#1E57BE",
    upper95: "#1E57BE",
    lower95: "#006D77",
    upper80: "#9FC0FF",
    lower80: "#83C5BE",
  };

  return (
    <div style={{ display: "inline-block" }}>
      {title ? (
        <div style={{ textAlign: "center", fontWeight: 600, marginBottom: 6 }}>
          {title}
        </div>
      ) : null}

      <ResponsiveContainer width={width} height={height}>
        <ComposedChart
          data={data}
          margin={{ top: 8, right: 12, bottom: 44, left: 8 }} // extra bottom for legend/axis
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />

          <XAxis
            dataKey="year"
            type="number"
            domain={[minX, maxX]}
            //ticks={[...new Set(years)]}
            allowDecimals={false}
            allowDuplicatedCategory={false}
            //interval={0}
            tick={{ fontSize: 10 }}
            axisLine={{ stroke: "#000" }}
            tickLine={{ stroke: "#000" }}
          />
          <YAxis
            width={52}
            tick={{ fontSize: 10 }}
            axisLine={{ stroke: "#000" }}
            tickLine={{ stroke: "#000" }}
            label={
              yLabel
                ? {
                    value: yLabel,
                    angle: -90,
                    position: "insideLeft",
                    offset: -2,
                    style: { fontSize: 10 },
                  }
                : undefined
            }
          />

          <Tooltip
            formatter={(v, n) =>
              typeof v === "number" ? Math.round(v * 1000) / 1000 : v
            }
          />
          <Legend
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{ paddingTop: 8, fontSize: 10 }}
          />

          {/* History (black) */}
          <Line
            type="monotone"
            dataKey="history"
            name="History"
            stroke={COLORS.history}
            strokeWidth={1.6}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />

          {/* Forecast (blue) */}
          <Line
            type="monotone"
            dataKey="forecast"
            name="Forecast"
            stroke={COLORS.forecast}
            strokeWidth={1.6}
            dot={{ r: 2 }}
            isAnimationActive={false}
            connectNulls
          />

          {/* CI star markers (only appear for forecast rows since history has nulls there) */}
          <Scatter
            name="Upper 95"
            data={data}
            dataKey="upper_95"
            shape={(p) => <StarDot {...p} fill={COLORS.upper95} size={8} />}
            isAnimationActive={false}
          />
          <Scatter
            name="Lower 95"
            data={data}
            dataKey="lower_95"
            shape={(p) => <StarDot {...p} fill={COLORS.lower95} size={8} />}
            isAnimationActive={false}
          />
          <Scatter
            name="Upper 80"
            data={data}
            dataKey="upper_80"
            shape={(p) => <StarDot {...p} fill={COLORS.upper80} size={7} />}
            isAnimationActive={false}
          />
          <Scatter
            name="Lower 80"
            data={data}
            dataKey="lower_80"
            shape={(p) => <StarDot {...p} fill={COLORS.lower80} size={7} />}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {yLabel ? (
        <div
          style={{
            fontSize: 11,
            color: "#555",
            marginTop: 6,
            textAlign: "center",
          }}
        >
          {yLabel}
        </div>
      ) : null}
    </div>
  );
}
